import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatCurrency, usePerm } from "../lib/providers";
import { PageHeader, Btn, StatusBadge, EmptyState, TextInput } from "../components/primitives";
import BulkActionBar, { Checkbox } from "../components/BulkActionBar";
import { downloadPdfBundle } from "../lib/bundle";
import { useConfirm } from "../components/ConfirmDialog";
import QuotesTabBar from "../components/QuotesTabBar";
import { Plus, PencilSimple, Trash, FileText, MagnifyingGlass, Ruler } from "@phosphor-icons/react";

export default function QuotesListPage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const { can, hidesPrices } = usePerm();
  const canCreate = can("quotes", "create");
  const canEdit = can("quotes", "edit");
  const canDelete = can("quotes", "delete");
  const canViewAufmasse = can("aufmasse", "view");
  const noPrices = hidesPrices("quotes");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = (search = "") => {
    setLoading(true);
    api.get(`/quotes${search ? `?q=${encodeURIComponent(search)}` : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const tid = setTimeout(() => load(q), 250);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const del = async (id, number) => {
    const ok = await confirm({
      title: `${t("confirmDeleteTitle")} ${number || ""}`,
      description: t("confirmHardDeleteDesc"),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/quotes/${id}`);
    load(q);
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDel = async () => {
    const ids = [...selected];
    const ok = await confirm({
      title: t("confirmDeleteTitle"),
      description: t("confirmDeleteMany").replace("{n}", ids.length),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.post("/quotes/bulk-delete", { ids });
    setSelected(new Set());
    load(q);
  };
  const bulkCopy = async () => {
    const ids = [...selected];
    await api.post("/quotes/bulk-duplicate", { ids });
    setSelected(new Set());
    load(q);
  };

  const filtered = filter === "all" ? items : items.filter((q) => q.status === filter);
  const counts = items.reduce((acc, q) => ({ ...acc, [q.status]: (acc[q.status] || 0) + 1 }), {});

  const chips = [
    { k: "all", label: lang === "de" ? "Alle" : "All", n: items.length },
    { k: "draft", label: t("draft"), n: counts.draft || 0 },
    { k: "sent", label: t("sent"), n: counts.sent || 0 },
    { k: "accepted", label: t("accepted"), n: counts.accepted || 0 },
    { k: "rejected", label: t("rejected"), n: counts.rejected || 0 },
  ];

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="06 / Sales"
        title={t("quotes")}
        description={lang === "de" ? "Alle erstellten Angebote auf einen Blick." : "All your quotes at a glance."}
        actions={
          canCreate ? (
            <Link to="/quotes/new">
              <Btn data-testid="new-quote-btn"><Plus size={16} /> {t("newQuote")}</Btn>
            </Link>
          ) : null
        }
      />

      {/* tab bar: Angebote / Aufmaße / MwSt-Sätze */}
      <QuotesTabBar active="quotes" />

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
          <TextInput data-testid="quotes-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        {chips.map((c) => (
          <button
            key={c.k}
            onClick={() => setFilter(c.k)}
            data-testid={`filter-${c.k}`}
            className={`px-3 h-8 text-xs rounded-sm border transition-colors ${
              filter === c.k
                ? "bg-[var(--brand)] text-white border-[var(--brand)]"
                : "bg-[var(--surface)] text-[var(--muted)] border-[var(--border)] hover:bg-[var(--row-hover)]"
            }`}
          >
            {c.label} <span className="opacity-60 ml-1">{c.n}</span>
          </button>
        ))}
      </div>

      <BulkActionBar
        count={selected.size}
        lang={lang}
        onClear={() => setSelected(new Set())}
        onCopy={canCreate ? bulkCopy : undefined}
        onDelete={canDelete ? bulkDel : undefined}
        onBundle={async () => {
          const ids = [...selected];
          const items = ids.map((id) => ({ type: "quote", id }));
          await downloadPdfBundle(items, `Angebote_Bundle_${new Date().toISOString().slice(0,10)}`, lang);
        }}
      />

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("noQuotes")}
          description={lang === "de" ? "Lege dein erstes Angebot an." : "Create your first quote."}
          action={canCreate ? <Link to="/quotes/new"><Btn data-testid="empty-new-quote"><Plus size={16} /> {t("createFirst")}</Btn></Link> : null}
        />
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                {(canEdit || canDelete) && <th className="w-8 pl-5"></th>}
                <th className="text-left font-medium px-5 py-3 label-overline">{t("number")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("title")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("customer")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("status")}</th>
                {!noPrices && <th className="text-right font-medium px-5 py-3 label-overline">{t("total")}</th>}
                {(canEdit || canDelete) && <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((qt) => (
                <tr key={qt.id} className={`border-t border-[var(--border)] transition-colors fade-up ${selected.has(qt.id) ? "bg-[var(--brand-soft)]" : "hover:bg-[var(--row-hover)]"}`}>
                  {(canEdit || canDelete) && (
                    <td className="pl-5">
                      <Checkbox checked={selected.has(qt.id)} onChange={() => toggleSel(qt.id)} testId={`sel-quote-${qt.id}`} />
                    </td>
                  )}
                  <td className="px-5 py-3 font-mono">{qt.number}</td>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/quotes/${qt.id}`} className="hover:underline" data-testid={`quote-link-${qt.id}`}>
                      {qt.title || <span className="text-[var(--muted-2)]">—</span>}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">{qt.customer_snapshot?.name || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={qt.status} lang={lang} /></td>
                  {!noPrices && <td className="px-5 py-3 text-right font-medium">{formatCurrency(qt.total, lang)}</td>}
                  {(canEdit || canDelete) && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {canEdit && <Link to={`/quotes/${qt.id}`}><Btn variant="ghost" className="h-8 px-2" data-testid={`edit-quote-${qt.id}`}><PencilSimple size={14} /></Btn></Link>}
                        {canDelete && <Btn variant="ghost" onClick={() => del(qt.id, qt.number)} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5" data-testid={`delete-quote-${qt.id}`}><Trash size={14} /></Btn>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
